import { Controller } from "@hotwired/stimulus"

/**
 * Guide Sidebar Controller
 *
 * 공개 문서 레이아웃(layouts/guide_docs)의 상단바·사이드바 인터랙션을 담당한다.
 * 모바일 드로어 개폐, 스크롤 위치에 따른 현재 섹션 표시,
 * 상단바 드롭다운(빠른이동·공개 범위) 개폐와 문서 주소 복사.
 *
 * 사이드바의 초기 상태(데스크탑 펼침 / 모바일 접힘)는 CSS 미디어쿼리가 확정하므로
 * 여기서 상태를 계산하지 않는다. 루트에 `--sidebar-open` 클래스를 붙였다 떼는 것이
 * 전부이고, 데스크탑에서는 미디어쿼리가 그 클래스를 무시한다.
 *
 * Targets:
 * - sidebar: 사이드바 요소
 * - backdrop: 오버레이 배경
 * - toggle: 햄버거 버튼 (aria-expanded 동기화)
 * - section: 본문 섹션 (스크롤 위치 관찰 대상)
 * - navItem: 목차 링크 (data-anchor 로 섹션과 짝지어진다)
 * - dropdown: 상단바 드롭다운 details (바깥 클릭·ESC 로 닫고, 한 번에 하나만 연다)
 * - copyButton: 문서 주소 복사 버튼 (결과 문구를 잠깐 바꿔 보여준다)
 */
export default class extends Controller {
  static targets = ["sidebar", "backdrop", "toggle", "section", "navItem", "dropdown", "copyButton"]

  static OPEN_CLASS = "--sidebar-open"
  static ACTIVE_CLASS = "--active"

  connect() {
    this.escapeHandler = this.handleEscape.bind(this)
    document.addEventListener("keydown", this.escapeHandler)

    // details 는 바깥을 눌러도 닫히지 않는다 — 그 부분만 거든다
    this.outsideClickHandler = this.handleOutsideClick.bind(this)
    document.addEventListener("click", this.outsideClickHandler)

    this.visibleAnchors = new Set()
    this.setupSectionObserver()

    // 관찰 띠가 화면 상단이라 마지막 섹션은 띠에 올라오지 못한 채 문서가 끝난다.
    // 바닥 도달을 따로 봐야 목차가 마지막 항목에서 멎는다.
    this.scrollHandler = this.handleScroll.bind(this)
    window.addEventListener("scroll", this.scrollHandler, { passive: true })
  }

  disconnect() {
    document.removeEventListener("keydown", this.escapeHandler)
    document.removeEventListener("click", this.outsideClickHandler)
    clearTimeout(this.copyResetTimer)
    window.removeEventListener("scroll", this.scrollHandler)
    this.observer?.disconnect()
  }

  // ========================================
  // 드로어 개폐
  // ========================================

  toggle() {
    this.isOpen() ? this.close() : this.open()
  }

  open() {
    this.element.classList.add(this.constructor.OPEN_CLASS)
    this.syncToggleState()
  }

  /**
   * 목차 링크를 눌렀을 때도 호출된다.
   * 데스크탑에서는 사이드바가 늘 펼쳐져 있어 클래스가 없으므로 무해하다.
   */
  close() {
    this.element.classList.remove(this.constructor.OPEN_CLASS)
    this.syncToggleState()
  }

  isOpen() {
    return this.element.classList.contains(this.constructor.OPEN_CLASS)
  }

  // ========================================
  // 문서 주소 복사
  // ========================================

  async copyLink(event) {
    // 섹션 앵커를 뺀 문서 주소 — 미리보기가 문서 단위 카드라 그쪽에 맞춘다
    const url = `${window.location.origin}${window.location.pathname}`
    const button = event.currentTarget
    const copied = await this.writeToClipboard(url)

    const original = button.dataset.originalLabel ?? button.textContent
    button.dataset.originalLabel = original
    button.textContent = copied ? "복사됨" : "복사 실패"
    clearTimeout(this.copyResetTimer)
    this.copyResetTimer = setTimeout(() => {
      button.textContent = button.dataset.originalLabel
    }, 1600)
  }

  /**
   * navigator.clipboard 는 보안 컨텍스트(https 또는 localhost)에만 있다.
   * 개발 서버는 http://<머신이름>:7001 로 붙어 여기 해당하지 않으므로,
   * 없을 때는 옛 execCommand 로 떨어진다. 폐기 예정 API 지만 대안이 없다.
   */
  async writeToClipboard(text) {
    if (window.isSecureContext && navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(text)
        return true
      } catch {
        // 권한 거부 등 — 아래 폴백으로 이어간다
      }
    }

    const field = document.createElement("textarea")
    field.value = text
    field.setAttribute("readonly", "")
    field.style.position = "fixed"
    field.style.opacity = "0"
    document.body.appendChild(field)
    field.select()
    let copied = false
    try {
      copied = document.execCommand("copy")
    } catch {
      copied = false
    }
    field.remove()
    return copied
  }

  handleOutsideClick(event) {
    this.dropdownTargets.forEach((panel) => {
      if (panel.open && !panel.contains(event.target)) panel.open = false
    })
  }

  /**
   * 상단바 드롭다운은 한 번에 하나만 연다 — 둘 다 오른쪽 끝에서 내려와 서로 겹친다.
   * details 의 toggle 이벤트는 버블하지 않지만 Stimulus 가 요소에 직접 건다.
   */
  closeOtherDropdowns(event) {
    if (!event.currentTarget.open) return
    this.dropdownTargets.forEach((panel) => {
      if (panel !== event.currentTarget) panel.open = false
    })
  }

  handleEscape(event) {
    const opened = this.dropdownTargets.filter((panel) => panel.open)
    if (event.key === "Escape" && opened.length > 0) {
      opened.forEach((panel) => { panel.open = false })
      return
    }
    if (event.key === "Escape" && this.isOpen()) {
      this.close()
      if (this.hasToggleTarget) this.toggleTarget.focus()
    }
  }

  syncToggleState() {
    if (!this.hasToggleTarget) return
    const open = this.isOpen()
    this.toggleTarget.setAttribute("aria-expanded", open ? "true" : "false")
    this.toggleTarget.setAttribute("aria-label", open ? "목차 닫기" : "목차 열기")
  }

  // ========================================
  // 스크롤 위치 표시
  // ========================================

  /**
   * 화면 상단 띠(상단바 아래 ~ 화면 35% 지점)에 걸린 섹션을 현재 섹션으로 본다.
   * 띠를 벗어나 아무것도 안 걸리는 순간에는 직전 표시를 유지한다 —
   * 긴 섹션 사이를 지날 때 목차가 깜빡이는 것을 막기 위해서다.
   */
  setupSectionObserver() {
    if (!("IntersectionObserver" in window)) return

    const topOffset = this.topbarHeight()
    this.observer = new IntersectionObserver(
      (entries) => this.handleIntersect(entries),
      { rootMargin: `-${topOffset}px 0px -65% 0px`, threshold: 0 }
    )

    // Stimulus 는 최초 마크업의 sectionTargetConnected 를 connect() 보다 먼저 호출한다.
    // 그때는 옵저버가 없으므로 여기서 이미 있는 섹션을 직접 등록해 준다.
    this.sectionTargets.forEach((section) => this.observer.observe(section))
  }

  sectionTargetConnected(element) {
    this.observer?.observe(element)
  }

  sectionTargetDisconnected(element) {
    this.observer?.unobserve(element)
    this.visibleAnchors.delete(element.id)
  }

  handleIntersect(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        this.visibleAnchors.add(entry.target.id)
      } else {
        this.visibleAnchors.delete(entry.target.id)
      }
    })
    this.updateActive()
  }

  handleScroll() {
    if (this.scrollTicking) return
    this.scrollTicking = true
    requestAnimationFrame(() => {
      this.scrollTicking = false
      this.updateActive()
    })
  }

  updateActive() {
    // 바닥에 닿으면 마지막 섹션을 읽고 있는 것으로 본다
    if (this.atPageBottom()) {
      const last = this.sectionTargets[this.sectionTargets.length - 1]
      if (last) this.markActive(last.id)
      return
    }

    if (this.visibleAnchors.size === 0) return

    // 문서 순서상 가장 위에 있는 것을 현재 섹션으로 삼는다
    const current = this.sectionTargets.find((s) => this.visibleAnchors.has(s.id))
    if (current) this.markActive(current.id)
  }

  atPageBottom() {
    const doc = document.documentElement
    return Math.ceil(window.scrollY + window.innerHeight) >= doc.scrollHeight - 2
  }

  markActive(anchor) {
    this.navItemTargets.forEach((item) => {
      item.classList.toggle(this.constructor.ACTIVE_CLASS, item.dataset.anchor === anchor)
    })
  }

  topbarHeight() {
    return this.element.querySelector(".dd-doc-topbar")?.offsetHeight ?? 56
  }
}
